import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Pencil, Pin, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { addFixedExpense, deleteFixedExpense, getFixedExpenses, updateFixedExpense } from "@/lib/api";
import { categoryColor, formatEUR } from "@/lib/format";

const ACCENT = "#00D4FF";
const FIXED_CATEGORIES = [
  "Casa e Affitto",
  "Bollette e Utenze",
  "Abbonamenti",
  "Tasse e Commissioni",
  "Salute",
  "Altro",
];

const EMPTY_FORM = { name: "", amount: "", day: "1", category: "Casa e Affitto" };

const fieldClass =
  "h-10 w-full rounded-[7px] border border-white/[0.12] bg-[#050508] px-3 text-[13px] font-light text-white placeholder:text-white/25 focus:border-[#00D4FF]/40 focus:outline-none focus:ring-1 focus:ring-[#00D4FF]/25";

export const FixedExpensesSection = ({ active }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null); // null = chiuso; {id?, ...} = aperto
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await getFixedExpenses());
    } catch {
      toast.error("Impossibile caricare le spese fisse");
      setData({ items: [] });
    }
  }, []);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const openAdd = () => {
    setConfirmId(null);
    setForm({ ...EMPTY_FORM });
  };

  const openEdit = (it) => {
    setConfirmId(null);
    setForm({ id: it.id, name: it.name, amount: String(it.amount), day: String(it.day), category: it.category });
  };

  const closeForm = () => setForm(null);

  const save = async () => {
    const name = (form.name || "").trim();
    const amount = parseFloat(String(form.amount).replace(",", "."));
    const day = parseInt(form.day, 10);
    if (!name) return toast.error("Inserisci un nome");
    if (!Number.isFinite(amount) || amount <= 0) return toast.error("Inserisci un importo valido");
    if (!Number.isInteger(day) || day < 1 || day > 31) return toast.error("Il giorno deve essere tra 1 e 31");

    setSaving(true);
    try {
      const payload = { name, amount, day, category: form.category };
      if (form.id) {
        await updateFixedExpense(form.id, payload);
        toast.success("Spesa fissa aggiornata");
      } else {
        await addFixedExpense(payload);
        toast.success("Spesa fissa aggiunta");
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteFixedExpense(id);
      setConfirmId(null);
      toast.success("Spesa fissa eliminata");
      await load();
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const items = data?.items || [];

  return (
    <div className="mt-7" data-testid="fixed-expenses-section">
      <div className="flex items-center justify-between">
        <span className="mono flex items-center gap-2 text-[12px] font-medium tracking-[0.1em]" style={{ color: ACCENT }}>
          <Pin size={13} />
          SPESE FISSE <span className="text-white/25">//</span> MANUALI
        </span>
        {!form && (
          <button
            onClick={openAdd}
            data-testid="fixed-expense-add-button"
            className="mono flex items-center gap-1.5 rounded-[6px] border border-[#00D4FF]/35 px-2.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.15em] text-[#00D4FF] hover:bg-[#00D4FF]/[0.06] active:scale-[0.97]"
          >
            <Plus size={12} /> Aggiungi
          </button>
        )}
      </div>
      <p className="mt-1.5 text-[11px] font-light leading-4 text-white/35">
        Solo informativo · non incide sulla proiezione. Quando arriva l'addebito reale in banca lo segnaliamo.
      </p>

      {/* form aggiungi/modifica */}
      <AnimatePresence initial={false}>
        {form && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div
              className="mt-3 space-y-2.5 rounded-[10px] border border-[#00D4FF]/20 bg-[#0A0A12] p-3"
              data-testid="fixed-expense-form"
            >
              <input
                autoFocus
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nome (es. Affitto)"
                maxLength={60}
                data-testid="fixed-expense-name-input"
                className={fieldClass}
              />
              <div className="flex gap-2">
                <div className="flex-1">
                  <input
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                    placeholder="Importo €"
                    inputMode="decimal"
                    data-testid="fixed-expense-amount-input"
                    className={fieldClass}
                  />
                </div>
                <div className="w-[110px]">
                  <input
                    value={form.day}
                    onChange={(e) => setForm((f) => ({ ...f, day: e.target.value }))}
                    placeholder="Giorno"
                    inputMode="numeric"
                    data-testid="fixed-expense-day-input"
                    className={fieldClass}
                  />
                </div>
              </div>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger
                  className="h-10 w-full rounded-[7px] border-white/[0.12] bg-[#050508] text-[13px] font-light text-white focus:ring-[#00D4FF]/25"
                  data-testid="fixed-expense-category-select"
                >
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5" style={{ background: categoryColor(form.category) }} />
                    <SelectValue placeholder="Categoria" />
                  </div>
                </SelectTrigger>
                <SelectContent className="rounded-[8px] border-white/[0.08] bg-[#10101A] text-white">
                  {FIXED_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`fixed-expense-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                      <span className="flex items-center gap-2 font-light">
                        <span className="h-1.5 w-1.5" style={{ background: categoryColor(c) }} />
                        {c}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2 pt-0.5">
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="fixed-expense-save-button"
                  className="mono flex h-9 flex-1 items-center justify-center gap-1.5 rounded-[6px] border border-[#00D4FF]/40 text-[11px] font-medium uppercase tracking-[0.15em] text-[#00D4FF] hover:bg-[#00D4FF]/[0.06] active:scale-[0.98] disabled:opacity-50"
                >
                  <Check size={13} /> {saving ? "Salvataggio…" : form.id ? "Salva modifiche" : "Aggiungi"}
                </button>
                <button
                  onClick={closeForm}
                  disabled={saving}
                  data-testid="fixed-expense-cancel-button"
                  className="mono flex h-9 items-center justify-center gap-1.5 rounded-[6px] border border-white/[0.1] px-3 text-[11px] font-medium uppercase tracking-[0.15em] text-white/55 hover:bg-white/[0.04] active:scale-[0.98]"
                >
                  <X size={13} /> Annulla
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* lista */}
      {data === null ? (
        <div className="mt-3 space-y-2" data-testid="fixed-expenses-loading">
          {[0, 1].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-[8px] border border-white/[0.06] bg-[#0D0D14]" />
          ))}
        </div>
      ) : items.length === 0 && !form ? (
        <p
          className="mono mt-4 rounded-[8px] border border-dashed border-white/[0.1] bg-[#0A0A12] px-3 py-4 text-center text-[12px] font-light text-white/40"
          data-testid="fixed-expenses-empty"
        >
          Nessuna spesa fissa. Aggiungi l'affitto o le bollette per averle sempre sott'occhio.
        </p>
      ) : (
        <div className="mt-3 space-y-2" data-testid="fixed-expenses-list">
          {items.map((it, i) => {
            const color = categoryColor(it.category);
            return (
              <div
                key={it.id}
                className="rounded-[10px] border border-white/[0.06] bg-[#0D0D14] px-3 py-3"
                data-testid={`fixed-expense-row-${i}`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="mono flex h-9 w-9 shrink-0 items-center justify-center rounded-[7px] border text-[12px]"
                    style={{ borderColor: `${color}55`, color, background: `${color}12` }}
                  >
                    {(it.name || "?").slice(0, 2).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-[13px] font-light text-white/85" data-testid="fixed-expense-name">
                        {it.name}
                      </p>
                      {it.detected && (
                        <span
                          className="caps-tag shrink-0 rounded-[4px] px-1.5 py-0.5 text-[#00FF87]"
                          style={{ border: "1px solid rgba(0,255,135,0.35)" }}
                          data-testid={`fixed-expense-detected-${i}`}
                          title={it.detected_match ? `Rilevato: ${it.detected_match.date} · ${formatEUR(it.detected_match.amount)}` : ""}
                        >
                          Rilevato in banca
                        </span>
                      )}
                    </div>
                    <p className="mono mt-0.5 text-[11px] tracking-[0.04em] text-white/35">
                      <span className="inline-flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-[2px]" style={{ backgroundColor: color }} />
                        {it.category}
                      </span>
                      <span className="mx-1.5 text-white/20">·</span>
                      il giorno {it.day}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <p className="mono mr-1 text-[14px] font-medium tabular-nums text-white/90">{formatEUR(it.amount)}</p>
                    <button
                      onClick={() => openEdit(it)}
                      data-testid={`fixed-expense-edit-${i}`}
                      aria-label="Modifica"
                      className="flex h-7 w-7 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/45 hover:bg-white/[0.05] hover:text-white/80"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={() => setConfirmId(confirmId === it.id ? null : it.id)}
                      data-testid={`fixed-expense-delete-${i}`}
                      aria-label="Elimina"
                      className="flex h-7 w-7 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/45 hover:bg-[#FF4D4D]/[0.08] hover:text-[#FF6B6B]"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>

                <AnimatePresence initial={false}>
                  {confirmId === it.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.15 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-2.5 flex items-center justify-between gap-3 rounded-[7px] border border-[#FF4D4D]/25 bg-[#FF4D4D]/[0.05] px-3 py-2">
                        <span className="text-[12px] font-light text-white/70">Eliminare “{it.name}”?</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => remove(it.id)}
                            data-testid={`fixed-expense-confirm-delete-${i}`}
                            className="mono rounded-[5px] border border-[#FF4D4D]/40 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.12em] text-[#FF6B6B] hover:bg-[#FF4D4D]/[0.1]"
                          >
                            Elimina
                          </button>
                          <button
                            onClick={() => setConfirmId(null)}
                            className="mono rounded-[5px] border border-white/[0.12] px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.12em] text-white/55 hover:bg-white/[0.05]"
                          >
                            Annulla
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}

          {items.length > 0 && (
            <p className="mono pt-1 text-right text-[11px] tracking-[0.04em] text-white/40" data-testid="fixed-expenses-total">
              Totale: <span className="text-white/80">{formatEUR(data.monthly_total)}</span>/mese
            </p>
          )}
        </div>
      )}
    </div>
  );
};
